import * as amplitude from "@amplitude/analytics-browser";
import type { ManagedReturnsTrialSubmissionInput } from "@redotech/merchant-sdk/rpc/schema/merchant-submissions/submit-managed-returns-trial";
import { useHandler } from "@redotech/react-util/hook";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { alertOnFailure } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoElevatedOnboarding } from "@redotech/redo-web/elevated-onboarding/redo-elevated-onboarding";
import { RedoElevatedOnboardingHeroPanel } from "@redotech/redo-web/elevated-onboarding/redo-elevated-onboarding-hero-panel";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcMutation } from "@redotech/redo-web/rpc-query/use-rpc-mutation";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { memo, useContext, useEffect, useRef, useState } from "react";
import { PlatformEvents } from "../../platform-events";
import warehouseImageUrl from "./assets/warehouse-graphic.png?url";
import {
  getAverageProcessingTimeLabel,
  isReturnProcessingAutomationEnabled,
} from "./managed-returns-trial-audience";
import { StepContent } from "./managed-returns-trial-pages";
import {
  getManagedReturnsTrialSubmissionInput,
  isManagedReturnsTrialSubmissionStep,
} from "./managed-returns-trial-submission";

export type ReturnsManagement =
  ManagedReturnsTrialSubmissionInput["returnsManagement"];
export type ProductHandling =
  ManagedReturnsTrialSubmissionInput["productHandling"];
export type StepId =
  | "hook"
  | "manage-today"
  | "processing"
  | "refulfillment"
  | "consolidation"
  | "confirmation";

export interface FormState {
  returnsManagement: ReturnsManagement | null;
  productHandling: ProductHandling | null;
}

interface StepMeta {
  id: StepId;
  title: string;
}

interface ManagedReturnsTrialModalProps {
  onClose: () => void;
  onComplete?: () => void;
}

const DEFAULT_FORM: FormState = {
  returnsManagement: null,
  productHandling: null,
};

const BASE_STEPS: StepMeta[] = [
  { id: "hook", title: "30-day free trial" },
  { id: "manage-today", title: "How you manage returns" },
  { id: "processing", title: "How processing works" },
];

const CONFIRMATION_STEP: StepMeta = {
  id: "confirmation",
  title: "We'll be in touch",
};

function visibleSteps(productHandling: ProductHandling | null): StepMeta[] {
  if (productHandling === "fulfill") {
    return [
      ...BASE_STEPS,
      { id: "refulfillment", title: "How refulfillment works" },
      CONFIRMATION_STEP,
    ];
  }
  if (productHandling === "consolidate") {
    return [
      ...BASE_STEPS,
      { id: "consolidation", title: "How consolidation works" },
      CONFIRMATION_STEP,
    ];
  }
  return BASE_STEPS;
}

export const ManagedReturnsTrialModal = memo(function ManagedReturnsTrialModal({
  onClose,
  onComplete,
}: ManagedReturnsTrialModalProps) {
  const team = useContext(TeamContext);
  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackedShownRef = useRef(false);
  const submissionMutation = useRpcMutation(RedoMerchantRpcClientContext, {
    method: "submitManagedReturnsTrial",
  });

  const isAutoProcessing = isReturnProcessingAutomationEnabled(
    team?.automation?.returnProcessing
      ? {
          enabled: team.automation.enable,
          exchanges: team.automation.returnProcessing.exchanges,
          refunds: team.automation.returnProcessing.refunds,
          storeCredit: team.automation.returnProcessing.store_credit,
        }
      : undefined,
  );
  const analyticsEndDate = Temporal.Now.plainDateISO();
  const processingStatsQuery = useRpcQuery(RedoMerchantRpcClientContext, {
    method: "getReturnOperationsKeyStats",
    args: {
      startDate: analyticsEndDate.subtract({ days: 90 }).toString(),
      endDate: analyticsEndDate.toString(),
      types: [ReturnTypeEnum.RETURN],
      salesChannels: [],
    },
    options: { enabled: !isAutoProcessing },
  });
  const averageProcessingTimeLabel = processingStatsQuery.data
    ? getAverageProcessingTimeLabel(
        processingStatsQuery.data.avgDaysToProcess,
        processingStatsQuery.data.numProcessedComplete,
      )
    : null;

  const steps = visibleSteps(form.productHandling);
  const safeIndex = Math.min(stepIndex, steps.length - 1);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const step = steps[safeIndex]!;
  const isConfirmation = step.id === "confirmation";
  const isLongFormStep =
    step.id === "processing" ||
    step.id === "refulfillment" ||
    step.id === "consolidation";

  // Confirmation is excluded from the total; the branch slide isn't in `steps`
  // until a handling choice is made, so count it explicitly beforehand.
  const progressTotal =
    form.productHandling === null ? BASE_STEPS.length + 1 : steps.length - 1;
  const progressPercent = ((safeIndex + 1) / progressTotal) * 100;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [safeIndex]);

  useEffect(() => {
    if (trackedShownRef.current) {
      return;
    }
    trackedShownRef.current = true;
    amplitude.track(PlatformEvents.MANAGED_RETURNS_TRIAL_SHOWN, {
      isAutoProcessing,
    });
  }, [isAutoProcessing]);

  const analyticsProperties = managedReturnsTrialAnalyticsProperties({
    form,
    isAutoProcessing,
  });

  const handleClose = useHandler(() => {
    if (!isConfirmation) {
      amplitude.track(PlatformEvents.MANAGED_RETURNS_TRIAL_DISMISSED, {
        ...analyticsProperties,
        stepId: step.id,
      });
    }
    onClose();
  });

  const handleBack = useHandler(() => {
    setStepIndex(Math.max(0, safeIndex - 1));
  });

  const handleContinue = useHandler(async () => {
    if (!isManagedReturnsTrialSubmissionStep(step.id)) {
      amplitude.track(PlatformEvents.MANAGED_RETURNS_TRIAL_STEP_ADVANCED, {
        ...analyticsProperties,
        stepId: step.id,
      });
      setStepIndex(safeIndex + 1);
      return;
    }

    const submissionInput = getManagedReturnsTrialSubmissionInput(
      step.id,
      form,
    );
    if (submissionInput === null) {
      return;
    }

    await alertOnFailure("Failed to submit your responses")(async () => {
      await submissionMutation.mutateAsync(submissionInput);
      amplitude.track(PlatformEvents.MANAGED_RETURNS_TRIAL_SUBMITTED, {
        isAutoProcessing,
        productHandling: submissionInput.productHandling,
        returnsManagement: submissionInput.returnsManagement,
      });
      amplitude.track(PlatformEvents.MANAGED_RETURNS_TRIAL_STEP_ADVANCED, {
        ...analyticsProperties,
        stepId: step.id,
      });
      setStepIndex(safeIndex + 1);
    });
  });

  const handleRestart = useHandler(() => {
    setForm(DEFAULT_FORM);
    setStepIndex(0);
  });

  const handleFinish = useHandler(() => {
    if (onComplete) {
      onComplete();
      return;
    }
    handleClose();
  });

  const selectReturnsManagement = useHandler((value: ReturnsManagement) => {
    setForm((prev) => ({ ...prev, returnsManagement: value }));
  });

  const selectProductHandling = useHandler((value: ProductHandling) => {
    setForm((prev) => ({ ...prev, productHandling: value }));
  });

  const continueDisabled =
    (step.id === "manage-today" && form.returnsManagement === null) ||
    (step.id === "processing" && form.productHandling === null);

  const primaryText =
    step.id === "hook"
      ? "Get started for free"
      : step.id === "refulfillment" || step.id === "consolidation"
        ? "Finish"
        : "Continue";

  return (
    <RedoElevatedOnboarding
      bodyScroll={false}
      dismissible
      footer={
        <OnboardingFooter
          continueDisabled={continueDisabled}
          isConfirmation={isConfirmation}
          isPending={submissionMutation.isPending}
          onBack={handleBack}
          onContinue={handleContinue}
          onFinish={handleFinish}
          onRestart={handleRestart}
          primaryText={primaryText}
          showBack={safeIndex > 0}
        />
      }
      footerFlush
      onCloseRequested={handleClose}
      productName="Redo Managed Returns"
      progressPercent={isConfirmation ? undefined : progressPercent}
      rightPanel={
        <RedoElevatedOnboardingHeroPanel imageUrl={warehouseImageUrl} />
      }
      showExitButton
    >
      <Flex grow={1} height="full" overflowY="auto" ref={scrollRef}>
        <Flex
          align="center"
          dir="column"
          height="full"
          justify={isLongFormStep ? "flex-start" : "center"}
          px="4xl"
          py="4xl"
          w="full"
        >
          <Flex dir="column" maxW={isLongFormStep ? "lg" : "md"} w="full">
            <StepContent
              averageProcessingTimeLabel={averageProcessingTimeLabel}
              form={form}
              isAutoProcessing={isAutoProcessing}
              onSelectProductHandling={selectProductHandling}
              onSelectReturnsManagement={selectReturnsManagement}
              stepId={step.id}
            />
          </Flex>
        </Flex>
      </Flex>
    </RedoElevatedOnboarding>
  );
});

function managedReturnsTrialAnalyticsProperties({
  form,
  isAutoProcessing,
}: {
  form: FormState;
  isAutoProcessing: boolean;
}) {
  return {
    isAutoProcessing,
    ...(form.productHandling === null
      ? {}
      : { productHandling: form.productHandling }),
    ...(form.returnsManagement === null
      ? {}
      : { returnsManagement: form.returnsManagement }),
  };
}

function OnboardingFooter({
  continueDisabled,
  isConfirmation,
  isPending,
  onBack,
  onContinue,
  onFinish,
  onRestart,
  primaryText,
  showBack,
}: {
  continueDisabled: boolean;
  isConfirmation: boolean;
  isPending: boolean;
  onBack: () => void;
  onContinue: () => void;
  onFinish: () => void;
  onRestart: () => void;
  primaryText: string;
  showBack: boolean;
}) {
  return (
    <Flex bgColor="primary" justify="space-between" p="3xl" w="full">
      <Flex>
        {isConfirmation ? (
          <RedoButton
            hierarchy="secondary"
            onClick={onRestart}
            size="lg"
            text="Start over"
          />
        ) : (
          showBack && (
            <RedoButton
              hierarchy="secondary"
              onClick={onBack}
              size="lg"
              text="Back"
            />
          )
        )}
      </Flex>
      {isConfirmation ? (
        <RedoButton
          hierarchy="primary"
          onClick={onFinish}
          size="lg"
          text="Done"
        />
      ) : (
        <RedoButton
          disabled={continueDisabled || isPending}
          hierarchy="primary"
          onClick={onContinue}
          pending={isPending}
          size="lg"
          text={primaryText}
        />
      )}
    </Flex>
  );
}
